import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Router from "next/router";

import KButton from "@kikoff/components/src/v1/buttons/KButton";
import IconList from "@kikoff/components/src/v1/info/IconList";
import useErrorConsumer from "@kikoff/hooks/src/useErrorConsumer";
import { cls, format } from "@kikoff/utils/src/string";

import Detail, { DetailsGroup } from "@component/dashboard/account/detail";
import { PageRegistration } from "@component/flows/hooks/usePageFlow";
import { selectCreditAccount } from "@feature/credit_line";
import {
  selectActiveSubscription,
  selectPlanName,
  updateOrders,
  useAutoRenewInfo,
} from "@feature/shopping";
import PageLayout from "@layout/component/page_layout";

import SubscriptionAutoRenewButton from "./subscriptions_autorenewal_button";

import styles from "./plan.module.scss";

interface PlanPageProps extends PageRegistration {}

export const getPlanIconItems = (
  planName: string,
  creditLimitCents: number,
  context?: "default" | "dispute_delivery"
) => [
  <>
    A Credit account with{" "}
    <span className="text:small+">{format.money(creditLimitCents)}</span>{" "}
    reported credit limit for lowering credit utilization and reporting payment
    history{" "}
    {context === "dispute_delivery" && <sup className="text:micro">1</sup>}
  </>,
  planName !== "Basic" && (
    <>
      <span className="text:small+">Monthly</span> 3-Bureau full credit reports
    </>
  ),
  <>
    <span className="text:small+">Weekly</span> credit score updates
  </>,
  planName !== "Basic" && (
    <>
      Dispute inaccurate or erroneous items to all{" "}
      <span className="text:small+">3 bureaus</span>{" "}
      {context === "dispute_delivery" && <sup className="text:micro">2</sup>}{" "}
      with free mailing
    </>
  ),
  planName === "Ultimate" && (
    <>
      Privacy Protection
      <div className={cls("text:mini", styles["icon-item-subtitle"])}>
        Remove your exposed data from dangerous sites and data brokers
      </div>
      <div className={cls("text:mini", styles["icon-item-subtitle"])}>
        Identity theft insurance up to $1,000,000
      </div>
    </>
  ),
  planName !== "Basic" && "Rent and utility reporting ",
];

export default function PlanPage({ page }: PlanPageProps) {
  const dispatch = useDispatch();
  const error = useErrorConsumer();
  const [subscription] = useSelector(selectActiveSubscription.load());

  const { autoRenewEnabled } = useAutoRenewInfo(subscription);

  const [planName] = useSelector(selectPlanName.load());
  const creditLine = useSelector(selectCreditAccount());

  useEffect(() => {
    dispatch(updateOrders()).catch(error.throw);
  }, []);

  return (
    <PageLayout
      title={planName + " plan"}
      onBack={() => {
        page.controller.history.back();
      }}
    >
      <div className="text:regular+ mb-2">Your Benefits</div>
      <IconList
        className="my-2 text:small"
        color="var(--primary)"
        density="default"
        size="1rem"
        icon=""
        items={getPlanIconItems(planName, creditLine.creditLimitCents).filter(
          Boolean
        )}
      />
      {subscription && (
        <>
          <div className="text:regular+ mb-2 mt-4">Details</div>
          <DetailsGroup
            variant="stacked"
            propsFor={{
              label: { className: "text:mini mb-0.5" },
              value: { className: "text:small" },
              detail: { className: "my-2" },
            }}
          >
            <Detail label="Plan">
              {`${subscription.planInfo?.durationMonths}-month plan at 
                ${format.money(creditLine.paymentSchedule?.monthlyPaymentCents)}
                per month`}
            </Detail>
            <Detail label="Enrolled Since">
              {format.date(subscription.openedAt, "Mmmm d, yyyy")}
            </Detail>
            <Detail label="Next payment due date">
              {format.date(creditLine.paymentDueDate, "Mmm d, yyyy")}
            </Detail>
            {autoRenewEnabled ? (
              <Detail label="Auto-renewal date">
                {format.date(subscription.autoRenew.renewalAt, "Mmm d, yyyy")}
              </Detail>
            ) : (
              <Detail label="Expiration date">
                {format.date(subscription.expiresAt, "Mmm d, yyyy")}
              </Detail>
            )}
          </DetailsGroup>

          <div className={styles["button-container"]}>
            <KButton
              type="button"
              className="m-3"
              variant="black"
              size="standard"
              onClick={() =>
                Router.replace("/dashboard/credit-account/change-plan")
              }
            >
              Change Plan
            </KButton>
            {subscription && subscription.autoRenew && (
              <SubscriptionAutoRenewButton subscription={subscription} />
            )}
          </div>
        </>
      )}
    </PageLayout>
  );
}
